import { addDoc, collection, query, where, orderBy, onSnapshot, doc, getDoc } from "firebase/firestore";
import { databasePromise } from "configuration";
import { logAnalyticsEvent } from "./logAnalyticsEvent";

const isRestaurantOpen = (operatingHours) => {
  if (!operatingHours || Object.keys(operatingHours).length === 0) return true;
  const days = ["sunday", "monday", "tuesday", "wednesday", "thursday", "friday", "saturday"];
  const now = new Date();
  const todayHours = operatingHours[days[now.getDay()]];
  if (!todayHours || todayHours.closed) return false;
  const { open, close } = todayHours;
  if (!open || !close) return true;
  const [openH, openM] = open.split(":").map(Number);
  const [closeH, closeM] = close.split(":").map(Number);
  const currentMins = now.getHours() * 60 + now.getMinutes();
  return currentMins >= openH * 60 + openM && currentMins < closeH * 60 + closeM;
};

export const orderService = {
  /**
   * Place an order. Stores restaurantUserId so the customer can open a
   * chat with the vendor without needing an extra Firestore fetch.
   */
  placeOrder: async ({ cart, orderType, unavailableOption, note, subtotal, userId, estimatedPrepTime }) => {
    const db = await databasePromise;

    const lineItems = Object.entries(cart.items).map(([dealId, qty]) => {
      const meta = cart.meta?.[dealId] || {};
      return {
        dealId,
        qty,
        name: meta.name || null,
        price: meta.price || 0,
        imageUrl: meta.imageUrl || null,
        lineTotal: (meta.price || 0) * qty,
      };
    });

    const order = {
      userId,
      restaurantId: cart.restaurantId,
      restaurantName: cart.restaurantMeta?.name || null,
      restaurantLogoUrl: cart.restaurantMeta?.logoUrl || null,
      // Store the vendor's Firebase Auth UID so customers can open a chat
      // without needing to fetch the restaurant document again
      restaurantUserId: cart.restaurantMeta?.userId || null,
      items: lineItems,
      subtotal,
      orderType,
      unavailableOption,
      note: note || null,
      status: "pending",
      paymentMethod: "cash_on_delivery",
      estimatedPrepTime: estimatedPrepTime || null,
      createdAt: new Date(),
      updatedAt: new Date(),
    };

    const ref = await addDoc(collection(db, "orders"), order);

    logAnalyticsEvent("order_placed", {
      order_id: ref.id,
      restaurant_id: cart.restaurantId,
      restaurant_name: cart.restaurantMeta?.name || null,
      item_count: lineItems.length,
      subtotal,
      order_type: orderType,
    });

    return ref.id;
  },

  /**
   * Validate that the restaurant is active and currently open.
   * Returns { valid: true, restaurant } or { valid: false, reason: string }
   */
  validateCartRestaurant: async (restaurantId) => {
    if (!restaurantId) return { valid: false, reason: "No restaurant in cart." };
    try {
      const db = await databasePromise;
      const snap = await getDoc(doc(db, "restaurants", restaurantId));
      if (!snap.exists()) return { valid: false, reason: "This restaurant no longer exists." };

      const data = snap.data();

      if (["disabled", "deleted", "pendingDelete"].includes(data.status)) {
        return { valid: false, reason: "This restaurant is no longer available." };
      }
      if (data.temporarilyClosed) {
        return { valid: false, reason: "This restaurant is temporarily closed." };
      }
      if (!isRestaurantOpen(data.operatingHours)) {
        const days = ["sunday", "monday", "tuesday", "wednesday", "thursday", "friday", "saturday"];
        const hours = data.operatingHours?.[days[new Date().getDay()]];
        const hoursStr = hours?.open && hours?.close ? ` They open at ${hours.open}.` : "";
        return { valid: false, reason: `This restaurant is currently closed.${hoursStr}` };
      }

      return { valid: true, restaurant: { id: snap.id, ...data } };
    } catch {
      return { valid: false, reason: "Could not verify restaurant status." };
    }
  },

  subscribeToUserOrders: (userId, callback) => {
    let unsubscribe = () => {};
    databasePromise.then((db) => {
      const q = query(
        collection(db, "orders"),
        where("userId", "==", userId),
        orderBy("createdAt", "desc"),
      );
      unsubscribe = onSnapshot(q, (snap) => {
        const orders = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        callback(orders);
      });
    });
    return () => unsubscribe();
  },
};
